import {Alert, Button, DataStatus, Dialog, useTranslationContext} from 'platform/components';
import {Box, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {usePdfExport} from '../../hooks/usePdfExport';
import type {PdfExportResult} from '../../types/PdfExportResult';
import {PdfPreview} from '../PdfPreview/PdfPreview';

type PdfExportDialogProps = {
  title: string;
  description: string;
  create: (signal: AbortSignal) => Promise<PdfExportResult>;
  onClose: () => void;
};

const download = (url: string, filename: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
};

/** Mount only for an active export. Each mount owns one captured document and its resources. */
export function PdfExportDialog(props: PdfExportDialogProps) {
  const t = useTranslationContext();
  const {state, generate} = usePdfExport(props.create);
  const filename = state.status === 'ready' ? state.result.filename : props.title;

  const handleDownload = () => {
    if (state.status === 'ready') {
      download(state.url, state.result.filename);
    }
  };

  const handleOpen = () => {
    if (state.status === 'ready') {
      // Open synchronously from the click so the browser retains the user's activation.
      window.open(state.url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Dialog
      title={t('general.actions.preview')}
      isOpen
      onClose={props.onClose}
      size="xLarge"
      disableBodyPadding
      buttons={[
        {title: t('general.actions.close'), variant: 'secondary', onClick: props.onClose},
        {
          title: t('general.actions.open'),
          'aria-label': t('general.actions.open'),
          variant: 'primary',
          leftIcon: 'action/open_in_new',
          isDisabled: state.status !== 'ready',
          onClick: handleOpen,
        },
        {
          title: t('general.actions.save'),
          'aria-label': t('general.actions.save'),
          variant: 'secondary',
          leftIcon: 'file/download',
          isDisabled: state.status !== 'ready',
          onClick: handleDownload,
        },
      ]}
    >
      <VStack width="100%" minWidth={0} height="100%" minHeight={0}>
        <Box
          paddingHorizontal={[4, 6]}
          paddingVertical={3}
          backgroundColor="general.white"
          flexShrink={0}
        >
          <HStack spacing={3} align="center">
            <Box
              padding={2}
              backgroundColor="palettes.neutral.20.100"
              borderRadius="medium"
              flexShrink={0}
            >
              <Icon value="files_default/file_type_PDF_monochrome" size={6} />
            </Box>
            <VStack spacing={1} minWidth={0} grow={1}>
              <Text alternative noWrap title={filename}>
                {filename}
              </Text>
              <Text size="small" color="secondary">
                {props.description}
              </Text>
            </VStack>
          </HStack>
        </Box>
        <Box backgroundColor="palettes.neutral.30.100" flex={1} minHeight={0} overflow="hidden">
          {match(state)
            .with({status: 'loading'}, () => <DataStatus isLoading minHeight={80} />)
            .with({status: 'error'}, (error) => (
              <Box padding={[4, 6]}>
                <VStack spacing={4}>
                  <Alert
                    variant="error"
                    type="inline"
                    message={
                      error.recoveryMessageKey
                        ? `${t(error.messageKey)} ${t(error.recoveryMessageKey)}`
                        : t(error.messageKey)
                    }
                  />
                  <Show when={error.retryable}>
                    <HStack>
                      <Button title={t('general.actions.retry')} onClick={generate} />
                    </HStack>
                  </Show>
                </VStack>
              </Box>
            ))
            .with({status: 'ready'}, ({result}) => <PdfPreview bytes={result.bytes} />)
            .exhaustive()}
        </Box>
      </VStack>
    </Dialog>
  );
}
